/**
 * How long a restore waits for the page to grow tall enough before it gives up. It has to give up some time, because a
 * page can end up shorter than it was (e.g. with fewer rows on it), and so never get there.
 */
export const RESTORE_MAX_DURATION_MS = 15000;

// 'scroll' is deliberately not one of these, since our own scrollTo causes it
const USER_SCROLL_INTENT_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

// scrollHeight and clientHeight are rounded to whole pixels, whereas scrollY can have a fraction
const ROUNDING_TOLERANCE_PX = 1;

export interface ScrollRestore {
  /** the scroll position being restored */
  target: number,
  /** whether the restore is still going, i.e. neither complete nor cancelled */
  isPending: () => boolean,
  /** Gives up, because the user took over or navigated elsewhere */
  cancel: () => void
}

/** How far down the page the window can scroll, as the page is now */
const getMaxScrollY = (): number => document.documentElement.scrollHeight - document.documentElement.clientHeight;

/**
 * Whether the page is tall enough, as it is now, for the window to scroll as far as the position. If it isn't, the
 * window can only be scrolled as far as the page goes.
 */
export const isScrollPositionReachable = (position: number): boolean =>
  position - getMaxScrollY() <= ROUNDING_TOLERANCE_PX;

/**
 * Gets the window back to a scroll position it was at on a page that is being shown again, after the caller has
 * already scrolled there once, as soon as the page was rendered. That is only if the page was tall enough already (see
 * isScrollPositionReachable): otherwise the caller leaves the window at the top, rather than part of the way.
 *
 * That first scroll is not always enough, because the window can only scroll as far as the page is tall, and the page
 * may not have reached its full height by then:
 *  - It can still change synchronously after the caller has scrolled, within the same task (e.g. TabPanel switching
 *    tab in componentDidUpdate).
 *  - A page that has to load its data first, as when going Back from another section, stays short until the data
 *    arrives, possibly in stages.
 *
 * So the page is watched, and the window is scrolled to the position once the page is tall enough for it to be
 * reached. That happens once, and before the browser paints: a resize observer reports sizes after layout and before
 * painting, and always reports once straight after it starts observing. Until then the window is left alone, rather
 * than being dragged to the bottom of a page that is still growing.
 *
 * Interacting with the page - scrolling, keys, touching, clicking - hands control back to the user. A page that never
 * gets tall enough, because it has less on it than it did, is given up on after a while.
 */
export const restoreScrollPosition = (target: number): ScrollRestore => {
  let pending = true;
  // what has to be undone once the restore is over
  const teardown: (() => void)[] = [];

  const end = () => {
    pending = false;
    teardown.splice(0).forEach(undo => undo());
  };

  const restoreWhenTallEnough = () => {
    if (pending && isScrollPositionReachable(target)) {
      window.scrollTo(0, target);
      end();
    }
  };

  // The window scrolls the page, which is as tall as what is in the body. The body itself is only as tall as the
  // window (see body.css), so it is no use to watch.
  const observer = new ResizeObserver(restoreWhenTallEnough);
  Array.from(document.body.children).forEach(element => observer.observe(element));
  teardown.push(() => observer.disconnect());

  USER_SCROLL_INTENT_EVENTS.forEach(type => {
    window.addEventListener(type, end, { capture: true, passive: true });
    teardown.push(() => window.removeEventListener(type, end, { capture: true }));
  });

  const giveUp = setTimeout(end, RESTORE_MAX_DURATION_MS);
  teardown.push(() => clearTimeout(giveUp));

  return {
    target,
    isPending: () => pending,
    cancel: end
  };
};
